import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["banner"]
  static values = {
    caseId: Number,
    spent: Boolean,
    status: String,
    newCaseUrl: String
  }

  connect() {
    this.bindStatusSelect()
    this.maybeShowSpentGuide()

    if (!this.hasCaseIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: "CrmCaseAlertsChannel", crm_case_no_id: this.caseIdValue },
      { received: this.received.bind(this) }
    )
  }

  disconnect() {
    if (this.statusSelect) {
      this.statusSelect.removeEventListener("change", this.onStatusChange)
    }
    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription)
      this.subscription = null
    }
  }

  bindStatusSelect() {
    this.onStatusChange = this.onStatusChange.bind(this)
    this.statusSelect = this.element.closest("form")?.querySelector("#crm_case_no_status")
    if (this.statusSelect) {
      this.statusSelect.addEventListener("change", this.onStatusChange)
    }
  }

  onStatusChange(event) {
    if (!this.spentValue) return
    if (event.target.value === "RE-OPENED") {
      this.showSpentGuide()
      this.notifySpentCase()
    }
  }

  maybeShowSpentGuide() {
    if (!this.spentValue) return
    this.showSpentGuide()
  }

  showSpentGuide() {
    if (typeof window.showCrmSpentGuide === "function") {
      window.showCrmSpentGuide()
      return
    }

    const modal = document.getElementById("spent-case-guide-modal")
    if (!modal) return
    modal.classList.add("is-open")
    modal.setAttribute("aria-hidden", "false")
    document.body.style.overflow = "hidden"
  }

  notifySpentCase() {
    const message = "Wrong case. Open a new CRM for the 2nd trip — this case will close when you save."
    if (window.jQuery && typeof window.jQuery.notify === "function") {
      window.jQuery.notify(
        { icon: "nc-icon nc-bell-55", message },
        { type: "danger", delay: 12000, placement: { from: "top", align: "center" }, z_index: 1090 }
      )
    }
  }

  received(payload) {
    if (this.hasBannerTarget) {
      if (payload.html) {
        this.bannerTarget.innerHTML = payload.html
      } else if (payload.state) {
        this.bannerTarget.replaceChildren(this.bannerFor(payload.state))
      }
    }

    this.setApplyInputsDisabled(payload.state?.kind === "closed")
  }

  bannerFor(state) {
    const banner = document.createElement("div")
    banner.setAttribute("role", state.kind === "needs_target_date" ? "alert" : "status")

    if (state.kind === "closed") {
      if (state.applied) {
        banner.className = "crm-spent-board"
        const who = state.by_user || "another user"
        banner.textContent =
          `Wrong case. This case already used its date/team change (by ${who}). ` +
          "Close it, then open a new CRM case for the 2nd trip."
        if (this.hasNewCaseUrlValue) {
          const link = document.createElement("a")
          link.href = this.newCaseUrlValue
          link.className = "crm-spent-board__cta"
          link.textContent = "Open a new CRM case for the 2nd trip"
          banner.appendChild(link)
        }
      } else {
        banner.className = "alert alert-info crm-case-alert-banner"
        banner.textContent =
          "This case is closed, so the team box is hidden. That is not a missing permission. " +
          "If you opened this case or have User Rights ADMIN: set Status to RE-OPENED, Save, then pick New Delivery Team. " +
          "Otherwise ask the case owner or someone with User Rights ADMIN to reopen it — or open a new CRM case."
      }
    } else if (state.kind === "needs_target_date") {
      banner.className = "alert alert-warning crm-case-alert-banner"
      banner.textContent = "Action required: Select a new service date before applying this reschedule case."
    } else {
      banner.className = "alert alert-info crm-case-alert-banner"
      banner.textContent = "This CRM case is ready for date, team, or time-slot updates."
    }

    return banner
  }

  setApplyInputsDisabled(disabled) {
    this.element
      .closest("form")
      ?.querySelectorAll(
        "#crm_case_no_new_service_date, #crm_case_no_new_delivery_team, #crm_case_no_new_time_slot"
      )
      .forEach((input) => {
        input.disabled = disabled
      })
  }
}
